import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import HOC from '../../components/HOC/HOC';
import {
  DeleteSuccessfullyModal,
  SuccessfullyModal,
} from '../../components/Modals/Modals';
import Pagination from '../../components/Pagination/Pagination';
import styles from '../../css/style.module.css';
import { getApi } from '../../Repository/Api';
import { formatDateTime } from '../../utils/utils';
import TimesheetDownloadModal from './TimesheetDownloadModal';
import TimesheetDownloadSuccessModal from './TimesheetDownloadSuccessModal';
import TimesheetApproveModal from './TimesheetApproveModal';
import TimesheetFilters from './TimesheetFilters';
import {
  approveStaffJob,
  canApproveStaffJob,
  getApprovalColor,
  getApprovalLabel,
} from './timesheetJobActions';
import { buildTimesheetDownloadUrl, buildTimesheetListUrl } from './timesheetEndpoints';

import img2 from '../../assest/loading1.gif';

import { FaRegEdit } from 'react-icons/fa';
import { FaPlus } from 'react-icons/fa6';
import { IoSearchSharp } from 'react-icons/io5';
import { MdOutlineRemoveRedEye } from 'react-icons/md';

const columns = [
  'S.no',
  'User Name',
  'Employee Name',
  'Employee ID',
  'Start Date',
  'End Date',
  'Total Hours Worked',
  'Approval',
  'Status',
  'Action',
];

const COLUMN_COUNT = columns.length;

const actionButtonStyle = {
  padding: '4px 8px',
  fontSize: '11px',
  fontWeight: 600,
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  color: '#FFFFFF',
  whiteSpace: 'nowrap',
};

const TimesheetManagement = () => {
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showDownloadSuccessModal, setShowDownloadSuccessModal] = useState(false);
  const [downloadedFilters, setDownloadedFilters] = useState({
    status: '',
    fromDate: '',
    toDate: '',
  });
  const [show2, setShow2] = useState(false);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({
    status: '',
    fromDate: '',
    toDate: '',
  });

  const [pagination, setPagination] = useState({
    limit: 10,
    totalPages: 1,
    page: 1,
    hasPrevPage: false,
    hasNextPage: false,
  });
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(null);
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const fetchRequestIdRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(prev => {
        if (prev === search) return prev;
        setPagination(p => (p.page === 1 ? p : { ...p, page: 1 }));
        return search;
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPagination(prevPagination => ({
      ...prevPagination,
      totalPages: data?.data?.totalPages,
      hasPrevPage: data?.data?.hasPrevPage,
      hasNextPage: data?.data?.hasNextPage,
    }));
  }, [data]);

  useEffect(() => {
    const controller = new AbortController();
    const requestId = ++fetchRequestIdRef.current;

    const load = async () => {
      await getApi(
        buildTimesheetListUrl(
          pagination.page,
          pagination.limit,
          debouncedSearch,
          appliedFilters.status,
          appliedFilters.fromDate,
          appliedFilters.toDate
        ),
        {
          signal: controller.signal,
          setResponse: response => {
            if (requestId === fetchRequestIdRef.current) {
              setData(response);
            }
          },
          setLoading: value => {
            if (requestId === fetchRequestIdRef.current) {
              setLoading(value);
            }
          },
          errorMsg: 'Failed to fetch data!',
          onError: () => {
            if (requestId === fetchRequestIdRef.current) {
              setData([]);
            }
          },
        }
      );
    };

    load();

    return () => {
      controller.abort();
    };
  }, [
    pagination.page,
    pagination.limit,
    debouncedSearch,
    appliedFilters.status,
    appliedFilters.fromDate,
    appliedFilters.toDate,
    listRefreshKey,
  ]);

  const refreshList = () => {
    setListRefreshKey(prev => prev + 1);
  };

  const handleSearchChange = e => {
    setSearch(e.target.value);
  };

  const handleStatusChange = status => {
    setSelectedStatus(status);
    setAppliedFilters(prev => ({ ...prev, status }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({
      status: selectedStatus,
      fromDate,
      toDate,
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setSelectedStatus('');
    setFromDate('');
    setToDate('');
    setAppliedFilters({ status: '', fromDate: '', toDate: '' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleDownloadRequest = () => {
    if (!fromDate || !toDate) return;
    setShowDownloadModal(true);
  };

  const handleDownloadConfirm = useCallback(async () => {
    if (!fromDate || !toDate) return;

    setDownLoading(true);

    const data1 = await getApi(
      buildTimesheetDownloadUrl(fromDate, toDate, selectedStatus),
      {
        setResponse: () => {},
        setLoading: setDownLoading,
        errorMsg: 'Failed to download timesheet!',
      }
    );

    setDownLoading(false);

    if (data1?.data) {
      const fileUrl = `https://at-lo.in/api/${data1.data}`;
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = 'timesheet.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setDownloadedFilters({
        status: selectedStatus,
        fromDate,
        toDate,
      });
      setShowDownloadModal(false);
      setShowDownloadSuccessModal(true);

      setTimeout(() => {
        setShowDownloadSuccessModal(false);
      }, 5000);
    }
  }, [fromDate, toDate, selectedStatus]);

  const handleApproveRequest = item => {
    setPendingApproval(item);
    setShowApproveModal(true);
  };

  const handleApproveConfirm = async () => {
    if (!pendingApproval?._id) return;

    setActionLoadingId(pendingApproval._id);
    const ok = await approveStaffJob(pendingApproval._id, { onSuccess: refreshList });
    setActionLoadingId(null);

    if (ok) {
      setShowApproveModal(false);
      setPendingApproval(null);
    }
  };

  const handleApproveModalClose = () => {
    if (actionLoadingId) return;
    setShowApproveModal(false);
    setPendingApproval(null);
  };

  const getJobStatusColor = jobStatus => {
    if (jobStatus === 'Completed') return '#00AD1D';
    if (jobStatus === 'Pending') return '#FFA800';
    if (jobStatus === 'Started') return '#1E88E5';
    if (jobStatus === 'Upcoming') return '#FF0E0E';
    return 'black';
  };

  return (
    <>
      <TimesheetDownloadModal
        show={showDownloadModal}
        onHide={() => !downloading && setShowDownloadModal(false)}
        status={selectedStatus}
        fromDate={fromDate}
        toDate={toDate}
        downloading={downloading}
        onConfirm={handleDownloadConfirm}
      />
      <TimesheetDownloadSuccessModal
        show={showDownloadSuccessModal}
        onHide={() => setShowDownloadSuccessModal(false)}
        status={downloadedFilters.status}
        fromDate={downloadedFilters.fromDate}
        toDate={downloadedFilters.toDate}
      />
      <TimesheetApproveModal
        show={showApproveModal}
        onHide={handleApproveModalClose}
        onConfirm={handleApproveConfirm}
        approving={actionLoadingId === pendingApproval?._id}
        staffName={
          pendingApproval?.staff
            ? `${pendingApproval.staff.firstName || ''} ${pendingApproval.staff.lastName || ''}`.trim()
            : ''
        }
        customerName={
          pendingApproval?.user
            ? `${pendingApproval.user.firstName || ''} ${pendingApproval.user.lastName || ''}`.trim()
            : ''
        }
      />
      <DeleteSuccessfullyModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        text="delete Timesheet"
      />
      <SuccessfullyModal
        show={show2}
        handleClose={() => setShow2(false)}
        text={'Successfully Deleted'}
      />
      <div className={styles.jobmanagementCotainer}>
        <div className={styles.jobtoppart}>
          <div className={styles.pagetitle}>
            <h6>Timesheet Management</h6>
            <div className={styles.searchbarnav}>
              <IoSearchSharp color="#8A8A8A" size={20} />
              <input
                type="search"
                placeholder="Search"
                value={search}
                onChange={handleSearchChange}
              />
            </div>
          </div>
          <div className={styles.rightbuttons}>
            <div className={styles.rightbuttons2} onClick={() => navigate('/addtimesheet')}>
              <FaPlus color="#FFFFFF" />
              <p>Add Timesheet</p>
            </div>
          </div>
        </div>

        <TimesheetFilters
          selectedStatus={selectedStatus}
          onStatusChange={handleStatusChange}
          fromDate={fromDate}
          toDate={toDate}
          onFromDateChange={setFromDate}
          onToDateChange={setToDate}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          onDownload={handleDownloadRequest}
          downloading={downloading}
          hasActiveFilters={
            !!selectedStatus ||
            !!fromDate ||
            !!toDate ||
            !!appliedFilters.status ||
            !!appliedFilters.fromDate ||
            !!appliedFilters.toDate
          }
        />

        <div className={styles.jobchildcontainer}>
          <div className={styles.tablecontainer}>
            <table className={styles.table1}>
              <thead>
                <tr>
                  {columns.map((label, index) => (
                    <th key={index} className={styles.tableHeader}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={COLUMN_COUNT} style={{ textAlign: 'center', padding: '1rem' }}>
                      <img src={img2} alt="" width={100} height={100} />
                    </td>
                  </tr>
                ) : data?.data?.docs?.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMN_COUNT} style={{ textAlign: 'center', padding: '1rem' }}>
                      No data available.
                    </td>
                  </tr>
                ) : (
                  data?.data?.docs?.map((item, rowIndex) => (
                    <tr key={item?._id || rowIndex} className={styles.tableRow}>
                      <td className={styles.tableCell}>{rowIndex + 1}</td>
                      <td className={styles.tableCell}>
                        {item?.user?.firstName} {item?.user?.lastName}
                      </td>
                      <td className={styles.tableCell}>
                        {item?.staff?.firstName} {item?.staff?.lastName}
                      </td>
                      <td className={styles.tableCell}>{item?.staff?.Id}</td>
                      <td className={styles.tableCell}>{formatDateTime(item?.fromTime)}</td>
                      <td className={styles.tableCell}>{formatDateTime(item?.toTime)}</td>
                      <td className={styles.tableCell}>{item?.workingHours}</td>
                      <td
                        className={styles.tableCell}
                        style={{
                          color: getApprovalColor(item),
                          fontWeight: '600',
                        }}
                      >
                        {getApprovalLabel(item)}
                      </td>
                      <td
                        className={styles.tableCell}
                        style={{
                          color: getJobStatusColor(item?.jobStatus),
                          fontWeight: '600',
                        }}
                      >
                        {item.jobStatus === 'Pending' ? 'Pending' : item?.jobStatus}
                      </td>
                      <td>
                        <div className={styles.tableaction}>
                          {canApproveStaffJob(item) && (
                            <button
                              type="button"
                              style={{ ...actionButtonStyle, background: '#1E88E5' }}
                              disabled={actionLoadingId === item?._id}
                              onClick={() => handleApproveRequest(item)}
                            >
                              {actionLoadingId === item?._id ? '...' : 'Approve'}
                            </button>
                          )}
                          <Link to={`/timesheet-Management/view/${item?._id}`}>
                            <MdOutlineRemoveRedEye size={20} />
                          </Link>
                          <Link to={`/timesheet-Management/edit/${item?._id}`}>
                            <FaRegEdit size={20} style={{ cursor: 'pointer', color: '#e9b44a' }} />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            limit={pagination.limit}
            totalPages={pagination.totalPages}
            page={pagination.page}
            hasPrevPage={pagination.hasPrevPage}
            hasNextPage={pagination.hasNextPage}
            onPageChange={newPage => setPagination({ ...pagination, page: newPage })}
          />
        </div>
      </div>
    </>
  );
};

export default HOC(TimesheetManagement);
